<template>
  <BittsContainer
    class="c-connect-data-source-card"
    :class="{
      [pointbreak as string]: true,
      crm: variant === 'crm',
      best: isBestOption,
    }"
  >
    <div>
      <BittsTag
        v-if="tagText"
        class="absolute top-12 right-6 block"
        color="secondary"
      >
        {{ tagText }}
      </BittsTag>
    </div>
    <!-- Content  -->
    <div class="content">
      <div>
        <BittsSvg
          v-if="variant !== 'crm'"
          class="w-48 h-48"
          :svg="`${variant}Icon`"
        />
        <BittsSvg
          v-else
          class="w-48 h-48"
          :svg="isChosenCrmSupported ? chosenCrm + 'Icon' : 'dataSourceIcon'"
        />
      </div>
      <template v-if="variant === 'crm'">
        <div class="flex justify-center font-bold">
          <h2
            :class="{
              'text-lg': isMobile,
              'text-xl': !isMobile,
            }"
          >
            {{ connectionText }}
          </h2>
        </div>
        <div>
          <div class="flex items-center gap-8 text-neutral-text-weak">
            <FontAwesomeIcon
              class="text-success-accent"
              :icon="['far', 'check']"
            />
            <p>Synced with real-time business</p>
          </div>
          <div class="flex items-center gap-8 text-neutral-text-weak">
            <FontAwesomeIcon
              class="text-success-accent"
              :icon="['far', 'check']"
            />
            <p>Optimized use of Crossbeam</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Footer with button  -->
    <div class="flex flex-col items-center gap-8">
      <BittsCheckbox
        :checked="isSandbox"
        @input="isSandbox = !isSandbox"
        v-if="isSandboxShown"
        label="I'm connecting to a sandbox"
        class="mb-12"
        :disabled="variant === 'crm' && isConnectCrmDisabled"
      />
      <BittsButton
        v-if="variant !== 'crm' || isChosenCrmSupported"
        :text="connectBtnText"
        class="w-full"
        size="small"
        :variant="connectBtnVariant"
        @click="handleClickConnectBtn"
        :data-testid="`add-${variant}-btn`"
        :disabled="variant === 'crm' && isConnectCrmDisabled"
      />
      <BittsDropdown
        v-if="variant === 'crm'"
        class="crm-btn-dropdown"
        parent-class="w-[368px]"
        :options="crmOptions.filter((crm) => crm.value !== chosenCrm)"
        @selection="handleChooseCrm"
      >
        <template #prompt>
          <BittsButton
            :text="
              isChosenCrmSupported ? 'Connect another CRM' : 'Choose a CRM'
            "
            variant="outline"
            type="neutral"
            size="small"
            :disabled="viewOnly"
            class="w-full"
          />
        </template>
      </BittsDropdown>
    </div>
  </BittsContainer>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCheckbox,
  BittsContainer,
  BittsDropdown,
  BittsSvg,
  BittsTag,
} from '@crossbeam/bitts/';
import { usePointbreak, useScreenSize } from '@crossbeam/pointbreak';

import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useNewOnboardingDataSource from '@/composables/useNewOnboardingDataSource';
import {
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { useFeedsStore } from '@/stores';
import { FeedIntegrationType } from '@/types/feeds';

type Variant = 'crm' | 'googleSheets' | 'csv';
const { variant } = defineProps<{
  variant: Variant;
}>();

const { googleFeed } = storeToRefs(useFeedsStore());

const {
  chosenCrm,
  chosenCrmFriendlyName,
  crmOptions,
  isChosenCrmSupported,
  isConnectCrmDisabled,
  isChosenCrmProxied,
  isSandbox,
  viewOnly,
  handleAddCsv,
  handleChooseCrm,
  handleConnectCrm,
} = useNewOnboardingDataSource();

const { isMobile } = useScreenSize();

const isSandboxShown = computed(
  () =>
    variant === 'crm' &&
    chosenCrm.value === SALESFORCE_DATA_SOURCE_TYPE &&
    !isChosenCrmProxied.value,
);

const tagText = computed(() => {
  if (variant === 'crm') {
    return 'Recommended';
  }
  if (variant === 'googleSheets' && !isChosenCrmSupported.value) {
    return 'Best alternative';
  }
  return null;
});

const isBestOption = computed(
  () =>
    (variant === 'crm' && isChosenCrmSupported.value) ||
    (variant === 'googleSheets' &&
      !isChosenCrmSupported.value &&
      ['no_crm', 'not_listed'].includes(chosenCrm.value as string)),
);

const connectionText = computed(() =>
  isChosenCrmSupported.value
    ? `Connect ${chosenCrmFriendlyName.value}`
    : 'Choose a crm',
);

const connectBtnText = computed(() => {
  if (variant === 'crm')
    return isConnectCrmDisabled.value
      ? 'Connected already'
      : connectionText.value;
  if (variant === 'googleSheets') return 'Connect a Google sheet';
  return 'Upload a CSV file';
});
const connectBtnVariant = computed(() => {
  if (isBestOption.value) return 'fill';
  return 'outline';
});

const router = useRouter();
async function handleClickConnectBtn() {
  if (variant === 'crm') {
    await handleConnectCrm(
      chosenCrmFriendlyName.value
        ? (chosenCrm.value as FeedIntegrationType)
        : SALESFORCE_DATA_SOURCE_TYPE,
    );
  }
  if (variant === 'googleSheets') {
    const isConnected =
      !!googleFeed.value && googleFeed.value?.status !== 'CONN_CHECK_FAILED';
    isConnected
      ? router.push({ name: 'onboarding-google-sheets' })
      : handleConnectCrm(GOOGLE_SHEETS_DATA_SOURCE_TYPE);
  }
  if (variant === 'csv') handleAddCsv();
}
const pointbreak = usePointbreak();
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.c-connect-data-source-card {
  @apply flex flex-col justify-between p-24 relative;

  .bitts-checkbox span .bitts-checkbox-with-label {
    @apply text-neutral-text-weak;
  }

  .content {
    @apply w-full flex flex-col items-center justify-center gap-12;
  }

  &.crm {
    @apply bg-neutral-50 w-[420px] border-none;
  }

  &.crm.sm,
  &.crm.xs {
    @apply w-full max-w-[420px];
  }

  &:not(.crm) {
    @apply h-full min-h-[180px];
  }

  &.best {
    @apply bg-neutral-50 border-solid border-2 border-secondary-border;
  }

  .crm-btn-dropdown,
  .crm-btn-dropdown span {
    @apply w-full;
  }
}
</style>
